<template>
  <BaseModal
    title="before you begin..."
    :show="showAccessModal"
    max-width="xl"
    as="form"
    :closeable="!isSubmitting"
    @close="$emit('closeAccessModal', false)"
    @submitted="() => handleSubmit()"
  >
    <p class="body-md">
      To help ensure you receive any CE credit available for this course, please
      verify your license information and review the refund policy below before
      continuing.
    </p>

    <output
      v-if="isLoadingLicenses" 
      aria-live="polite"
      aria-atomic="true"
      class="flex justify-center py-4"
    >
      <BaseLoadingIcon
        :visible="true"
        aria-hidden="true"
      />
      <span class="sr-only">Loading your license information</span>
    </output>

    <template v-else>
      <div 
        v-if="hasCompleteLicenses" 
        class="flex flex-col gap-y-4"
      >
        <h3 class="heading-xs">
          Confirm Your License
        </h3>
        <p class="body-sm">
          Additional licenses can be added or edited on your profile.
        </p>
        <div class="overflow-x-auto">
          <div class="overflow-hidden rounded-lg border border-gray-500">
            <table class="min-w-full divide-y divide-gray-500">
              <thead class="bg-gray-200">
                <tr>
                  <th
                    scope="col"
                    class="body-sm p-2 text-left font-semibold"
                    v-text="'License Type'"
                  />
                  <th
                    scope="col"
                    class="body-sm p-2 text-left font-semibold"
                    v-text="'License Number'"
                  />
                  <th
                    scope="col"
                    class="body-sm p-2 text-left font-semibold"
                    v-text="'State or Province'"
                  />
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-500">
                <tr 
                  v-for="license in licenses" 
                  :key="license.license_id"
                >
                  <td 
                    class="body-sm p-2" 
                    v-text="license.license_type"
                  />
                  <td 
                    class="body-sm p-2" 
                    v-text="license.license_number"
                  />
                  <td 
                    class="body-sm p-2" 
                    v-text="license.license_state" 
                  />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div 
        v-else 
        class="flex flex-col gap-y-4"
      >
        <h3 class="heading-xs">
          Add Your License
        </h3>

        <BaseFormErrorSummary 
          :errors="errors" 
          :submit-count="submitCount" 
        />

        <BaseStateSelect
          id="licenseState"
          v-model="values.licenseState"
          :country-id="countryId"
          label="License State or Province"
          name="licenseState"
          required
          validation-rules=""
        />

        <BaseSelectInput
          id="licenseType"
          v-model="values.licenseType"
          default-option-text="Select a license type"
          label="License Type"
          name="licenseType"
          required
          :disabled="licenseTypeDisabled"
        >
          <option
            v-for="profession in licenseTypes"
            :key="profession.id"
            :value="profession.id"
            v-text="profession.profession_name"
          />
        </BaseSelectInput>

        <BaseTextInput
          id="licenseNumber"
          v-model="values.licenseNumber"
          label="License Number"
          name="licenseNumber"
          required
          :disabled="licenseNumberDisabled"
        />
      </div>

      <div class="flex flex-col gap-y-4">
        <h3 class="heading-xs">
          Refund Disclaimer
        </h3>
        <p class="body-md">
          No refunds are given once the training materials are accessed.
          National Alliance of Wound Care and Ostomy (NAWCO) credentialing exam:
          It is your responsibility to ensure you meet NAWCO's testing
          eligibility requirements before starting your course. Eligibility
          information can be found at
          https://nawccb.org/wound-care-certification.
        </p>
        <BaseCheckboxInput
          id="refundDisclaimer"
          v-model="values.refundDisclaimer"
          :input-value="values.refundDisclaimer ?? false"
          label="I understand that my course is available for 180 days from the date I completed payment and that by accessing the training modules I am ineligible for a course refund."
          name="refundDisclaimer"
          required
          validation-rules="required"
        />
      </div>
    </template>

    <template #footer>
      <button
        :disabled="isSubmitting || isLoadingLicenses"
        type="submit"
        class="btn btn--primary"
        v-text="`Launch Course`"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeMount } from 'vue'
import { useForm } from 'vee-validate'

import BaseCheckboxInput from '@js/components/global/forms/inputs/BaseCheckboxInput.vue'
import BaseModal from '@js/components/global/BaseModal.vue'
import BaseStateSelect from '@js/components/global/forms/BaseStateSelect.vue'
import BaseSelectInput from '@js/components/global/forms/inputs/BaseSelectInput.vue'
import BaseTextInput from '@js/components/global/forms/inputs/BaseTextInput.vue'
import BaseFormErrorSummary from '@js/components/global/forms/BaseFormErrorSummary.vue'
import BaseLoadingIcon from '@js/components/global/BaseLoadingIcon.vue'

import { setRegisterAttestation } from '@js/api/courses/setRegisterAttestation'
import { upsertCustomerLicense } from '@js/api/customer/upsertCustomerLicense'
import { getCustomerLicenses } from '@js/api/customer/getCustomerLicenses'
import { getCustomerLicenseProfessions } from '@js/api/customer/getCustomerLicenseProfession'
import { log } from '@js/utils/logger'

import type { CustomerLicense } from '@js/api/types/customer/CustomerLicense'
import type { CustomerLicenseProfession } from '@js/api/types/customer/CustomerLicenseProfession'

defineOptions({
  name: 'RefundDisclaimer',
})

const props = defineProps<{
  showAccessModal: boolean;
  registrationId: number;
  customerId: number;
  countryId: string;
}>()

const emit = defineEmits<{
  closeAccessModal: [accepted: boolean];
}>()

const isLoading = ref(false)
const isLoadingLicenses = ref(false)
const licenses = ref<CustomerLicense[]>([])
const licenseTypes = ref<CustomerLicenseProfession[]>([])
const licenseTypeDisabled = ref(true)
const licenseNumberDisabled = ref(true)

const hasCompleteLicenses = computed(() =>
  licenses.value.some(
    l => !!l.license_type && !!l.license_state && !!l.license_number,
  ),
)

// incomplete license used as the seed for form pre-population
const seedLicense = computed(() => {
  if (hasCompleteLicenses.value) return null
  return licenses.value.find(l => !l.license_number) ?? null
})

// coordinates pre-population: set before triggering the licenseState watch so the
// watch can apply the type after professions are loaded asynchronously.
const pendingLicenseTypeId = ref<string | null>(null)

const {
  values,
  errors,
  submitCount,
  isSubmitting,
  setFieldValue,
  handleSubmit: handleValidationSubmit,
} = useForm({
  initialValues: {
    licenseState: '',
    licenseType: '',
    licenseNumber: '',
    refundDisclaimer: false,
  },
})

watch(
  () => values.licenseState,
  async (newState) => {
    if (!newState) return

    setFieldValue('licenseType', '', false)
    setFieldValue('licenseNumber', '', false)
    licenseTypeDisabled.value = true
    licenseNumberDisabled.value = true

    try {
      const professions = await getCustomerLicenseProfessions(newState)
      licenseTypes.value = professions
      licenseTypeDisabled.value = professions.length === 0

      if (pendingLicenseTypeId.value !== null) {
        const hasPendingLicenseType = professions.some(
          p => String(p.id) === pendingLicenseTypeId.value,
        )
        if (hasPendingLicenseType) {
          setFieldValue('licenseType', pendingLicenseTypeId.value, false)
          licenseNumberDisabled.value = false
        }
        pendingLicenseTypeId.value = null
      }
    } catch (error) {
      log({
        message: 'Failed to load license types',
        file: 'src/wcei/js/components/account/dashboard/RefundDisclaimer.vue',
        error,
      })
    }
  },
)

watch(
  () => values.licenseType,
  (newValue) => {
    licenseNumberDisabled.value = !newValue
  },
)

const handleSubmit = handleValidationSubmit(async (): Promise<void> => {
  try {
    isLoading.value = true

    if (!hasCompleteLicenses.value && values.licenseState && values.licenseType && values.licenseNumber) {
      const selectedLicenseType = licenseTypes.value.find(
        p => String(p.id) === String(values.licenseType),
      )
      const professionId = Number.parseInt(values.licenseType, 10)

      try {
        await upsertCustomerLicense({
          customerId: props.customerId,
          licenseId: seedLicense.value?.license_id ?? null,
          licenseNumber: values.licenseNumber,
          licenseType:
            selectedLicenseType?.profession_name ?? values.licenseType,
          licenseState: values.licenseState,
          license_profession_id: professionId,
        })
      } catch (upsertError) {
        // License save failed; log but do not block course access
        log({
          message: 'Could not save license information',
          file: 'src/wcei/js/components/account/dashboard/RefundDisclaimer.vue',
          error: upsertError,
        })
      }
    }

    await setRegisterAttestation({ registrationId: props.registrationId })

    emit('closeAccessModal', true)
  } catch (error) {
    log({
      message: 'Could not accept refund disclaimer',
      file: 'src/wcei/js/components/account/dashboard/RefundDisclaimer.vue',
    })
  } finally {
    isLoading.value = false
  }
})

onBeforeMount(async () => {
  try {
    isLoadingLicenses.value = true
    licenses.value = await getCustomerLicenses(props.customerId)

    const toPrePopulate = seedLicense.value
    if (toPrePopulate?.license_state) {
      if (toPrePopulate.license_profession_id) {
        pendingLicenseTypeId.value = String(
          toPrePopulate.license_profession_id,
        )
      }
      setFieldValue('licenseState', toPrePopulate.license_state, false)
    }
  } catch (error) {
    log({
      message: 'Could not load customer licenses',
      file: 'src/wcei/js/components/account/dashboard/RefundDisclaimer.vue',
      error,
    })
  } finally {
    isLoadingLicenses.value = false
  }
})
</script>
